import { useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Play, Clock, Sparkles, Gamepad2 } from "lucide-react";
import { games } from "../data/games";
import { useSeo } from "../hooks/useSeo";
import GameArt from "../components/GameArt";

const BASE_URL = "https://conjureflow.app";

const sectionReveal = {
  initial: { opacity: 0, y: 40, filter: "blur(10px)" },
  whileInView: { opacity: 1, y: 0, filter: "blur(0px)" },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

const GamePageContent = ({ game, t, lang }) => {
  const mainRef = useRef(null);
  const titleRef = useRef(null);
  const isLive = game.status === "live";
  const path = `/giochi/${game.slug}`;

  useSeo({
    title: game.seo.title[lang],
    description: game.seo.description[lang],
    path,
    jsonLd: [
      ...(isLive
        ? [
            {
              "@context": "https://schema.org",
              "@type": "VideoGame",
              name: game.name,
              url: `${BASE_URL}${path}`,
              description: game.seo.description[lang],
              applicationCategory: "Game",
              genre: game.genre.en,
              inLanguage: ["it", "en"],
              sameAs: game.url,
              publisher: { "@id": `${BASE_URL}/#organization` },
            },
          ]
        : []),
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Conjureflow", item: `${BASE_URL}/` },
          { "@type": "ListItem", position: 2, name: game.name, item: `${BASE_URL}${path}` },
        ],
      },
    ],
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    const rafId = requestAnimationFrame(() => {
      titleRef.current?.focus();
    });
    return () => cancelAnimationFrame(rafId);
  }, [game.slug]);

  return (
    <main
      id="main-content"
      ref={mainRef}
      tabIndex={-1}
      className="outline-none"
      data-testid={`game-page-${game.id}`}
    >
      <nav aria-label="Breadcrumb" className="max-w-7xl mx-auto px-6 md:px-12 pt-28 md:pt-36">
        <ol
          data-testid="game-breadcrumb"
          className="flex items-center gap-3 text-xs font-bold tracking-[0.2em] uppercase text-white/40"
        >
          <li>
            <Link
              to="/#giochi"
              data-testid="breadcrumb-catalog-link"
              className="hover:text-[#00F0FF] transition-colors duration-300"
            >
              {t.game.breadcrumbCatalog}
            </Link>
          </li>
          <li aria-hidden="true">/</li>
          <li aria-current="page" className="text-[#00F0FF]">
            {game.name}
          </li>
        </ol>
      </nav>

      <section className="relative px-6 md:px-12 pt-10 md:pt-14 pb-20 md:pb-28 overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full opacity-20 blur-[140px]"
          style={{ backgroundColor: game.colors.from }}
        />
        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <div className="overflow-hidden">
              <motion.h1
                ref={titleRef}
                tabIndex={-1}
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ type: "spring", damping: 22, stiffness: 90, delay: 0.15 }}
                className="font-display font-black uppercase tracking-tighter leading-[0.95] text-5xl md:text-6xl lg:text-7xl outline-none"
                data-testid="game-title"
              >
                {game.name}
              </motion.h1>
            </div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.45 }}
              className="mt-5 text-sm font-bold tracking-[0.25em] uppercase"
              style={{ color: game.colors.to }}
              data-testid="game-tagline"
            >
              {game.tagline[lang]}
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6 }}
              className="mt-6 text-base md:text-lg leading-relaxed text-white/70 max-w-xl"
            >
              {game.description[lang]}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.75 }}
              className="mt-10"
            >
              {isLive ? (
                <a
                  href={game.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="game-play-cta"
                  className="btn-magic inline-flex items-center gap-3 rounded-full px-8 py-4 font-display font-bold text-sm tracking-wide uppercase text-white"
                >
                  <Play className="w-4 h-4 fill-current" />
                  {t.game.play}
                </a>
              ) : (
                <div>
                  <span
                    data-testid="game-soon-cta"
                    className="inline-flex items-center gap-3 rounded-full px-8 py-4 font-display font-bold text-sm tracking-wide uppercase text-white/40 border border-white/15 cursor-not-allowed"
                    aria-disabled="true"
                  >
                    <Clock className="w-4 h-4" />
                    {t.game.soon}
                  </span>
                  <p className="mt-5 text-sm text-white/50 max-w-md">{t.game.soonNote}</p>
                </div>
              )}
            </motion.div>
          </div>

          <motion.figure
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="glass rounded-3xl overflow-hidden"
          >
            <div className="aspect-[16/10]" role="img" aria-label={`${game.name} artwork`}>
              <GameArt game={game} />
            </div>
          </motion.figure>
        </div>
      </section>

      <section aria-labelledby="about-heading" className="px-6 md:px-12 py-20 md:py-28">
        <motion.div {...sectionReveal} className="max-w-3xl mx-auto">
          <p className="text-sm font-bold tracking-[0.25em] uppercase text-[#00F0FF] mb-5">
            {t.game.about}
          </p>
          <h2
            id="about-heading"
            className="font-display font-bold tracking-tight text-3xl md:text-4xl mb-8"
          >
            {game.name}
          </h2>
          <p className="text-base md:text-lg leading-relaxed text-white/70" data-testid="game-long-description">
            {game.longDescription[lang]}
          </p>
        </motion.div>
      </section>

      <section aria-labelledby="features-heading" className="px-6 md:px-12 py-20 md:py-28">
        <div className="max-w-5xl mx-auto">
          <motion.h2
            {...sectionReveal}
            id="features-heading"
            className="font-display font-bold tracking-tight text-3xl md:text-4xl mb-12"
          >
            {t.game.features}
          </motion.h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="game-features-list">
            {game.features.map((feature, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="glass rounded-2xl p-6 flex items-start gap-4"
              >
                <Sparkles className="w-5 h-5 mt-0.5 shrink-0 text-[#D000FF]" strokeWidth={1.5} />
                <span className="text-base text-white/80">{feature[lang]}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      <section aria-labelledby="modes-heading" className="px-6 md:px-12 py-20 md:py-28">
        <div className="max-w-5xl mx-auto">
          <motion.h2
            {...sectionReveal}
            id="modes-heading"
            className="font-display font-bold tracking-tight text-3xl md:text-4xl mb-12"
          >
            {t.game.modes}
          </motion.h2>
          <ul className="flex flex-wrap gap-4" data-testid="game-modes-list">
            {game.modes.map((mode, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="glass rounded-full px-6 py-3 flex items-center gap-3 text-sm font-semibold text-white/80"
              >
                <Gamepad2 className="w-4 h-4 text-[#00F0FF]" strokeWidth={1.5} />
                {mode[lang]}
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      {game.gallery.length > 0 && (
        <section aria-labelledby="gallery-heading" className="px-6 md:px-12 py-20 md:py-28">
          <div className="max-w-5xl mx-auto">
            <motion.h2
              {...sectionReveal}
              id="gallery-heading"
              className="font-display font-bold tracking-tight text-3xl md:text-4xl mb-12"
            >
              {t.game.gallery}
            </motion.h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              {game.gallery.map((item, i) => (
                <motion.figure
                  key={i}
                  {...sectionReveal}
                  className="glass rounded-2xl overflow-hidden"
                >
                  <div
                    className="aspect-[16/10]"
                    role="img"
                    aria-label={item.caption[lang]}
                  >
                    <GameArt game={{ ...game, art: item.art }} />
                  </div>
                  <figcaption className="p-4 text-xs tracking-widest uppercase text-white/40">
                    {item.caption[lang]}
                  </figcaption>
                </motion.figure>
              ))}
            </div>
          </div>
        </section>
      )}

      <section aria-labelledby="details-heading" className="px-6 md:px-12 py-20 md:py-28">
        <motion.div {...sectionReveal} className="max-w-5xl mx-auto">
          <h2
            id="details-heading"
            className="font-display font-bold tracking-tight text-3xl md:text-4xl mb-12"
          >
            {t.game.details}
          </h2>
          <dl className="grid grid-cols-1 sm:grid-cols-3 gap-8" data-testid="game-details">
            <div className="glass rounded-2xl p-6">
              <dt className="text-xs font-bold tracking-[0.2em] uppercase text-white/40 mb-2">
                {t.game.genreLabel}
              </dt>
              <dd className="text-base text-white/85">{game.genre[lang]}</dd>
            </div>
            <div className="glass rounded-2xl p-6">
              <dt className="text-xs font-bold tracking-[0.2em] uppercase text-white/40 mb-2">
                {t.game.platformLabel}
              </dt>
              <dd className="text-base text-white/85">{t.game.platformValue}</dd>
            </div>
            <div className="glass rounded-2xl p-6">
              <dt className="text-xs font-bold tracking-[0.2em] uppercase text-white/40 mb-2">
                {t.game.statusLabel}
              </dt>
              <dd className="text-base text-white/85">
                {isLive ? t.catalog.liveBadge : t.catalog.soonBadge}
              </dd>
            </div>
          </dl>
        </motion.div>
      </section>

      <div className="px-6 md:px-12 pb-24">
        <Link
          to="/#giochi"
          data-testid="back-to-catalog-link"
          className="inline-flex items-center gap-3 text-sm font-bold tracking-widest uppercase text-white/50 hover:text-[#00F0FF] transition-colors duration-300"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={2} />
          {t.game.back}
        </Link>
      </div>
    </main>
  );
};

const GameNotFound = ({ t }) => {
  useSeo({
    title: `${t.game.notFound} | Conjureflow`,
    description: t.meta.description,
    path: "/404",
    noindex: true,
  });

  return (
    <main
      id="main-content"
      data-testid="game-not-found"
      className="min-h-screen flex flex-col items-center justify-center px-6 text-center"
    >
      <h1 className="font-display font-black uppercase tracking-tighter text-4xl md:text-6xl mb-8">
        {t.game.notFound}
      </h1>
      <Link
        to="/"
        data-testid="not-found-home-link"
        className="btn-magic inline-flex items-center gap-3 rounded-full px-8 py-4 font-display font-bold text-sm tracking-wide uppercase text-white"
      >
        {t.game.back}
      </Link>
    </main>
  );
};

const GamePage = ({ t, lang }) => {
  const { slug } = useParams();
  const game = games.find((g) => g.slug === slug);

  if (!game) {
    return <GameNotFound t={t} />;
  }

  return <GamePageContent game={game} t={t} lang={lang} />;
};

export default GamePage;
